import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Popover
} from '@mui/material';
import {
  Delete,
  DragIndicator,
  DateRange,
  Create
} from '@mui/icons-material';

export interface SignatureField {
  id: string;
  type: 'signature' | 'date';
  x: number;
  y: number;
  width: number;
  height: number;
  signer?: string;
  label?: string;
}

interface DocumentSignatureFieldProps {
  field: SignatureField;
  onUpdate: (field: SignatureField) => void;
  onDelete: () => void;
  onDragStart: () => void;
  isEditing: boolean;
  scale?: number;
}

const DocumentSignatureField: React.FC<DocumentSignatureFieldProps> = ({
  field,
  onUpdate,
  onDelete,
  onDragStart,
  isEditing,
  scale = 1
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [labelInput, setLabelInput] = useState(field.label || '');
  const fieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLabelInput(field.label || '');
  }, [field.label]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isEditing) return;
    e.preventDefault();
    setIsDragging(true);
    onDragStart();

    const startX = e.clientX;
    const startY = e.clientY;
    const startFieldX = field.x;
    const startFieldY = field.y;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = (moveEvent.clientX - startX) / scale;
      const deltaY = (moveEvent.clientY - startY) / scale;
      
      onUpdate({
        ...field,
        x: startFieldX + deltaX,
        y: startFieldY + deltaY
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleEditLabel = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    if (labelInput !== field.label) {
      onUpdate({
        ...field,
        label: labelInput
      });
    }
  };

  const getFieldIcon = () => {
    return field.type === 'signature' ? <Create /> : <DateRange />;
  };

  const getFieldColor = () => {
    return field.type === 'signature' ? '#1976d2' : '#388e3c';
  };

  return (
    <>
      <Box
        ref={fieldRef}
        onMouseDown={handleMouseDown}
        sx={{
          position: 'absolute',
          left: field.x,
          top: field.y,
          width: field.width,
          height: field.height,
          border: `2px dashed ${getFieldColor()}`,
          borderRadius: 1,
          bgcolor: isEditing ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
          cursor: isEditing ? 'move' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: isDragging ? 'none' : 'all 0.2s',
          '&:hover': {
            bgcolor: isEditing ? 'rgba(25, 118, 210, 0.15)' : 'rgba(0, 0, 0, 0.03)',
            boxShadow: isEditing ? 2 : 1
          }
        }}
      >
        {/* Field content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            pointerEvents: 'none'
          }}
        >
          {getFieldIcon()}
          {field.label && (
            <Typography variant="caption" fontWeight="bold" color={getFieldColor()}>
              {field.label}
            </Typography>
          )}
          {field.signer && (
            <Typography variant="caption" color="text.secondary">
              {field.signer}
            </Typography>
          )}
        </Box>

        {/* Editing controls */}
        {isEditing && (
          <Box
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
              display: 'flex',
              gap: 0.5,
              bgcolor: 'white',
              borderRadius: 1,
              boxShadow: 2,
              border: `1px solid ${getFieldColor()}`
            }}
          >
            <IconButton
              size="small"
              onClick={handleEditLabel}
              sx={{ pointerEvents: 'auto' }}
            >
              <Typography variant="caption">ABC</Typography>
            </IconButton>
            <IconButton
              size="small"
              onClick={onDelete}
              sx={{ pointerEvents: 'auto', color: 'error.main' }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Label edit popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <TextField
            fullWidth
            size="small"
            label="Field Label"
            value={labelInput}
            onChange={(e) => setLabelInput(e.target.value)}
            placeholder="e.g., Signature Here"
            autoFocus
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleClosePopover();
              }
            }}
          />
        </Box>
      </Popover>
    </>
  );
};

export default DocumentSignatureField;

